import React from 'react';
import {
    Typography,
    TextField,
    Box,
    RadioGroup,
    FormControlLabel,
    Radio,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
} from '@mui/material';
import { styled } from '@mui/system';
import {
    Smartphone,
    Phone,
    ChevronDown,
    Info as InfoIcon,
    CheckCircle,
    MessageSquare,
    Zap,
    Monitor,
    Tablet,
} from 'lucide-react';
import { motion } from 'framer-motion';

// Styled components
const SectionContainer = styled(motion.div)(({ theme, borderColor = theme.palette.primary.main, bgColor = theme.palette.primary.light }) => ({
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: '#FFFFFF',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #eaeaea',
    borderLeft: `4px solid ${borderColor}`,
    boxShadow: theme.shadows[1],
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: bgColor,
        opacity: 0.08,
        zIndex: 0,
        pointerEvents: 'none',
    },
    '& > *': {
        position: 'relative',
        zIndex: 1,
    },
}));

const LineContainer = styled(SectionContainer)(({ theme }) => ({
    borderLeftColor: theme.palette.primary.main,
    '&::before': {
        backgroundColor: theme.palette.primary.light,
    },
}));

const SectionTitle = styled(Typography)(({ theme, color = theme.palette.primary.main }) => ({
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(2),
    color: color,
    textAlign: 'center',
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -8,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '40px',
        height: '2px',
        backgroundColor: color,
        borderRadius: '1px',
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        transition: 'all 0.2s ease-in-out',
        backgroundColor: '#F9FAFB',
        '&:hover': {
            backgroundColor: '#F3F4F6',
        },
        '&.Mui-focused': {
            backgroundColor: '#FFFFFF',
            boxShadow: '0 0 0 3px rgba(255, 56, 92, 0.12)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 500,
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        fontWeight: 600,
    },
    '& .MuiInputAdornment-root': {
        '& svg': {
            color: theme.palette.text.secondary,
        },
    },
}));

const RadioOptionBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    background: '#F9FAFB',
    borderRadius: '10px',
    padding: '12px 16px',
    border: '1px solid #eaeaea',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(1),
    '& .MuiFormControlLabel-root': {
        marginLeft: 0,
        marginRight: 0,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}));

const EsimInfoBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.primary.light,
    borderRadius: '10px',
    border: `1px solid ${theme.palette.primary.light}`,
}));

const InstructionAccordion = styled(Accordion)(({ theme }) => ({
    marginTop: theme.spacing(3),
    border: '1px solid #eaeaea',
    borderRadius: '10px !important',
    boxShadow: 'none',
    overflow: 'hidden',
    '&::before': {
        display: 'none',
    },
    '& .MuiAccordionSummary-root': {
        minHeight: '56px',
        padding: '0 16px',
        '&:hover': {
            backgroundColor: '#F9FAFB',
        },
    },
    '& .MuiAccordionSummary-content': {
        gap: '8px',
    },
    '& .MuiAccordionDetails-root': {
        padding: '16px',
        backgroundColor: '#F9FAFB',
    },
}));

// Animation variants
const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 300,
            damping: 30,
            staggerChildren: 0.1
        }
    },
    exit: { opacity: 0, y: -20 }
};

const DeviceCompatibility = ({
    lineCount,
    imeis,
    esimOptions,
    imei2Values,
    imeiValidation,
    onImeiChange,
    onEsimChange,
    onImei2Change,
    selectedCarrier
}) => {
    // Render eSIM activation instructions
    const renderEsimActivationSteps = () => {
        return (
            <EsimInfoBox>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <InfoIcon size={22} color="#ff385c" style={{ marginTop: '2px' }} />
                    <Box>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#ff385c" }}>
                            eSIM Activation Process
                        </Typography>

                        <List dense disablePadding>
                            <ListItem disableGutters>
                                <ListItemIcon>
                                    <CheckCircle size={18} color="#ff385c" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2">
                                            <Box component="span" sx={{ fontWeight: 600 }}>Step 1:</Box> Your Account Owner will process your eSIM activation
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            <ListItem disableGutters>
                                <ListItemIcon>
                                    <MessageSquare size={18} color="#ff385c" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2">
                                            <Box component="span" sx={{ fontWeight: 600 }}>Step 2:</Box> You'll receive a <Box component="span" sx={{ fontWeight: 600 }}>text message</Box> or <Box component="span" sx={{ fontWeight: 600 }}>banner notification</Box>
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            <ListItem disableGutters>
                                <ListItemIcon>
                                    <Zap size={18} color="#ff385c" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2">
                                            <Box component="span" sx={{ fontWeight: 600 }}>Step 3:</Box> Follow the prompt instructions to complete setup
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        </List>
                    </Box>
                </Box>
            </EsimInfoBox>
        );
    };

    // Render IMEI lookup instructions
    const renderIMEIInstructions = () => {
        return (
            <InstructionAccordion>
                <AccordionSummary
                    expandIcon={<ChevronDown size={20} />}
                    sx={{ alignItems: 'center' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon size={20} color="#ff385c" />
                        <Typography sx={{ fontWeight: 500 }}>How to find your IMEI</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Your IMEI is a 15-digit unique identifier that helps verify device compatibility.
                    </Typography>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Phone size={20} color="#ff385c" />
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Using your dial pad:</Typography>
                            <Typography variant="body2" gutterBottom>
                                Enter <Box component="span" sx={{ fontWeight: 600 }}>*#06#</Box> on your phone's dial pad
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Smartphone size={20} color="#ff385c" />
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Android devices:</Typography>
                            <Typography variant="body2" gutterBottom>
                                Go to Settings → About device → Status
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Monitor size={20} color="#ff385c" />
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>iPhone devices:</Typography>
                            <Typography variant="body2" gutterBottom>
                                Go to Settings → General → About
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Tablet size={20} color="#ff385c" />
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Windows Phone:</Typography>
                            <Typography variant="body2" gutterBottom>
                                Go to Settings → About → More info
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#eef0ff', borderRadius: 1 }}>
                        <Typography variant="body2">
                            <strong>Note:</strong> For dual-SIM phones, you may have two IMEI numbers (IMEI1 and IMEI2).
                            The IMEI2 field will appear if you select "Yes" to eSIM activation.
                        </Typography>
                    </Box>
                </AccordionDetails>
            </InstructionAccordion>
        );
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
        >
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
                Will your phone{lineCount > 1 ? 's' : ''} work on this network?
            </Typography>
            <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4, maxWidth: '650px', mx: 'auto' }}>
                Your device{lineCount > 1 ? 's' : ''} must be unlocked. We recommend checking with {selectedCarrier}
                to see if your device{lineCount > 1 ? 's are' : ' is'} compatible on their network.
                Optionally, type in your IMEI{lineCount > 1 ? 's' : ''} and we'll be happy to check for you.
            </Typography>

            {imeis.map((imeiValue, index) => (
                <LineContainer
                    key={`line-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                >
                    <SectionTitle color="#ff385c">
                        Line {index + 1} Details
                    </SectionTitle>

                    <StyledTextField
                        fullWidth
                        label={`IMEI for Line ${index + 1}`}
                        value={imeiValue}
                        onChange={(e) => onImeiChange(index, e.target.value)}
                        error={!imeiValidation[index] && imeiValue !== ''}
                        helperText={!imeiValidation[index] && imeiValue !== '' ? 'Invalid IMEI (15 digits required)' : ''}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Smartphone size={20} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="center" sx={{ mt: 1, mb: 1 }}>
                        Interested in activating eSIM for Line {index + 1}?
                    </Typography>

                    <RadioOptionBox>
                        <RadioGroup
                            row
                            value={esimOptions[index]}
                            onChange={(e) => onEsimChange(index, e.target.value)}
                        >
                            <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                            <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                        </RadioGroup>
                    </RadioOptionBox>

                    <Collapse in={esimOptions[index] === 'yes'}>
                        {renderEsimActivationSteps()}
                        <StyledTextField
                            fullWidth
                            label={`IMEI2 + EID for Line ${index + 1}`}
                            value={imei2Values[index]}
                            onChange={(e) => onImei2Change(index, e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Smartphone size={20} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Collapse>
                </LineContainer>
            ))}

            {renderIMEIInstructions()}
        </motion.div>
    );
};

export default DeviceCompatibility;